/**
 * Approval Validation Schemas
 *
 * Zod validation schemas for approval workflow forms.
 * Used in ApprovalConfirmDialog for submit and decision operations.
 */

import {z} from 'zod';

/**
 * Submit for approval form schema
 *
 * Used when submitting a document for approval.
 * Validates approver selection and optional comment.
 */
export const submitApprovalSchema = z.object({
  /** At least one approver must be selected */
  approverIds: z
    .array(z.string().uuid())
    .min(1, 'At least one approver is required'),

  /** Approval rule (always 'all' for MVP) */
  approvalRule: z.enum(['all', 'any']).default('all'),

  /** Optional comment about submission */
  comment: z.string().optional(),
});

export type SubmitApprovalFormData = z.infer<typeof submitApprovalSchema>;

/**
 * Approval decision form schema
 *
 * Used when approving or rejecting a document.
 * Validates comment, electronic signature name, and confirmation checkbox.
 */
export const approvalDecisionSchema = z.object({
  /** Decision type (approved or rejected) */
  decision: z.enum(['approved', 'rejected']),

  /** Comment explaining the decision (REQUIRED) */
  comment: z.string().min(1, 'Comment is required'),

  /** User's typed full name for electronic signature */
  signatureName: z.string().min(1, 'Please type your full name'),

  /** Electronic signature confirmation checkbox */
  signatureConfirmed: z
    .boolean()
    .refine((val) => val, 'Please confirm your electronic signature'),
});

export type ApprovalDecisionFormData = z.infer<typeof approvalDecisionSchema>;

/**
 * Rejection decision form schema
 *
 * Used when rejecting a document.
 * Only requires comment - no e-signature needed for rejection.
 */
export const rejectDecisionSchema = z.object({
  /** Comment explaining the rejection (REQUIRED) */
  comment: z.string().min(1, 'Reason for rejection is required'),
});

export type RejectDecisionFormData = z.infer<typeof rejectDecisionSchema>;

/**
 * Reassign approvers form schema
 *
 * Used when reassigning approvers (placeholder for MVP).
 * Validates new approver selection and optional comment.
 */
export const reassignApproversSchema = z.object({
  /** New list of approvers (replaces existing) */
  approverIds: z
    .array(z.string().uuid())
    .min(1, 'At least one approver is required'),

  /** Optional comment about reassignment */
  comment: z.string().optional(),
});

export type ReassignApproversFormData = z.infer<typeof reassignApproversSchema>;
